import params from '@/lib/calculationparams';
import { format as formatCurrency } from '@/lib/currency';

export enum PayoutCategory {
  Salary,
  ReducedSalary,
  ExtraIncome,

  LGU,
  IVA,
  VVU,
  LAU,

  WBO,
  WHA,
  WEB,
  WEX,
  WET,
}

/**
 * Resolve a resultType CSS class name for coloring purposes
 *
 * @param category
 */
export function getPayoutClass(category: PayoutCategory) {
  switch (category) {
    case PayoutCategory.Salary:
    case PayoutCategory.ReducedSalary:
      return 'result-type--salary';
    case PayoutCategory.ExtraIncome:
      return 'result-type--salary-extra';

    case PayoutCategory.LGU:
      return 'result-type--uwv-lgu';
    case PayoutCategory.IVA:
      return 'result-type--uwv-iva';

    case PayoutCategory.LAU:
      return 'result-type--uwv-lau';
    case PayoutCategory.VVU:
      return 'result-type--uwv-vvu';

    case PayoutCategory.WBO:
      return 'result-type--nvschade-wbo';
    case PayoutCategory.WHA:
      return 'result-type--nvschade-wha';
    case PayoutCategory.WEB:
      return 'result-type--nvschade-web';
    case PayoutCategory.WEX:
      return 'result-type--nvschade-wex';
    case PayoutCategory.WET:
      return 'result-type--nvschade-wet';

    default:
      return '#dedede';
  }
}

/**
 * Get hover label for given payout category
 *
 * @param category
 */
export function getPayoutLabel(category: PayoutCategory): string {
  switch (category) {
    case PayoutCategory.Salary:
    case PayoutCategory.ReducedSalary:
      return 'Oud Loon';
    case PayoutCategory.ExtraIncome:
      return 'Nieuw Loon';

    case PayoutCategory.LGU:
      return 'WGA-loongerelateerde uitkering';
    case PayoutCategory.IVA:
      return 'IVA-uitkering';
    case PayoutCategory.VVU:
      return 'WGA-vervolg&shy;uitkering';
    case PayoutCategory.LAU:
      return 'WGA-loonaanvullings&shy;uitkering';

    case PayoutCategory.WBO:
      return 'NV schade<br>Werknemers&shy;verzekering';
    case PayoutCategory.WHA:
      return 'NV schade<br>Werknemers&shy;verzekering';
    case PayoutCategory.WEB:
      return 'NV schade<br>WIA-excedent&shy;verzekering';
    case PayoutCategory.WEX:
      return 'NV schade<br>WIA-excedent&shy;verzekering';
    case PayoutCategory.WET:
      return 'NV schade<br>WIA-excedent&shy;verzerkering Top';

    default:
      return '???';
  }
}

/**
 * Get a human-friendly name for the given Payout Category
 *
 * @param category
 */
export function getPayoutName(category: PayoutCategory): string {
  switch (category) {
    case PayoutCategory.Salary:
    case PayoutCategory.ReducedSalary:
      return 'Loondoorbetaling';
    case PayoutCategory.ExtraIncome:
      return 'Bijverdiensten';

    case PayoutCategory.LGU:
      return 'WGA-loongerelateerde uitkering UWV';
    case PayoutCategory.IVA:
      return 'IVA-uitkering UWV';
    case PayoutCategory.VVU:
      return 'WGA-vervolguitkering UWV';
    case PayoutCategory.LAU:
      return 'WGA-loonaanvullingsuitkering UWV';

    case PayoutCategory.WBO:
    case PayoutCategory.WHA:
      return 'WIA-werknemersverzekering NV schade';
    case PayoutCategory.WEB:
    case PayoutCategory.WEX:
      return 'WIA-excedentverzekering NV schade';
    case PayoutCategory.WET:
      return 'WIA-excedentverzekering Top NV schade';

    default:
      return '???';
  }
}

/**
 * Returns an array of paragraphs used as a description for the payout category
 *
 * @param category
 */
export function getPayoutDescription(category: PayoutCategory): string[] {
  const uwvMax = formatCurrency(params.uwv.maximumSalary);
  const nvExcMax = formatCurrency(params.nvschade.excedentMaximum);
  const nvExcTopMax = formatCurrency(params.nvschade.excedentTopMaximum);
  switch (category) {
    case PayoutCategory.Salary:
      return [
        'Uw loon in het eerste ziektejaar is 100% van uw laatstverdiende salaris. '
        + 'Uw werkgever is volgens de CAO verplicht het eerste ziektejaar 100% van uw '
        + 'salaris door te betalen.',
      ];
    case PayoutCategory.ReducedSalary:
      return [
        'Uw loon in het tweede ziektejaar is 80% of 90% van uw laatst-verdiende salaris. '
        + 'Dit is afhankelijk van uw werkgever',
      ];
    case PayoutCategory.ExtraIncome:
      return [
        'Uw nieuwe loon dat u verdient met werk naast uw eventuele uitkering van UWV.',
      ];

    case PayoutCategory.LGU:
      return [
        'U ontvangt deze uitkering van het UWV als u tussen de 35% en 80% arbeidsongeschikt bent of als '
        + 'u volledig, maar niet blijvend arbeidsongeschikt bent.',

        `De uitkering vult uw inkomen aan tot minimaal 70% van uw oude loon (tot ${uwvMax}). `
        + `De uitkering loopt minimaal ${params.uwv.lguMinPayoutDuration} maanden `
        + `en maximaal ${params.uwv.lguMaxPayoutDuration} maanden. `
        + 'De duur is afhankelijk van uw arbeidsverleden.',
      ];
    case PayoutCategory.IVA:
      return [
        'Bent u volledig én blijvend arbeidsongeschikt? '
        + 'Dan ontvangt u een IVA-uitkering van UWV.',

        `Deze uitkering vult uw inkomen aan tot minimaal 75% van uw oude loon (tot ${uwvMax}). `
        + 'U ontvangt de uitkering tot aan uw AOW-leeftijd.',
      ];
    case PayoutCategory.VVU:
      return [
        'Verdient u minder dan 50% van wat u volgens het UWV nog kan verdienen? '
        + 'Dan ontvangt u een WGA-vervolguitkering.',

        'Deze uitkering is gebaseerd op het minimum loon. '
        + 'U ontvangt de uitkering tot aan uw AOW-leeftijd.',
      ];
    case PayoutCategory.LAU:
      return [
        'Verdient u 50% of meer van wat u volgens het UWV nog kan verdienen? '
        + 'Of bent u tussen de 80% en 100% arbeidsongeschikt, maar niet blijvend? '
        + 'Dan ontvangt u een WGA-loonaanvullingsuitkering van UWV.',

        `De uitkering vult uw inkomen aan tot minimaal 70% van uw oude loon (tot ${uwvMax}). `
        + 'U ontvangt de uitkering tot uw AOW-leeftijd.',
      ];

    case PayoutCategory.WBO:
    case PayoutCategory.WHA:
      return [
        'U ontvangt een uitkering uit de WIA-werknemersverzekering bij een '
        + 'arbeidsongeschiktheidspercentage tussen de 15% en 80%. '
        + `Bij de WIA-werknemersverzekering is uw loon tot ${uwvMax} verzekerd.`,

        'Bent u tussen de 15% en 35% arbeidsongeschikt? '
        + 'Dan ontvangt u de uitkering maximaal 7,5 jaar.',

        'Bent u tussen de 35% en 80% arbeidsongeschikt? '
        + 'Dan ontvangt u de uitkering tot aan uw AOW-leeftijd.',
      ];
    case PayoutCategory.WEB:
    case PayoutCategory.WEX:
      return [
        'De WIA-excedentverzekering keert uit bij arbeidsongeschiktheid tussen de 15% en 100%. '
        + `Uw loon boven ${uwvMax} tot ${nvExcMax} is verzekerd.`,

        'Bent u tussen de 15% en 35% arbeidsongeschikt? '
        + 'Dan ontvangt u de uitkering maximaal 7,5 jaar of maximaal tot AOW-leeftijd als de '
        + 'periode tot uw AOW leeftijd 7,5 jaar of korter is.',
      ];
    case PayoutCategory.WET:
      return [
        'De WIA-excedentverzekering Top keert uit bij arbeidsongeschiktheid tussen de 35% en 100%. '
        + `Uw loon boven ${nvExcMax} tot ${nvExcTopMax} is verzekerd.`,
      ];

    default:
      return ['Unknown'];
  }
}

/**
 * Gets color for payout category.
 *
 * This only exists because it is needed for the PDF.
 * I really wanted to keep the colors in just one spot but I refuse to do CSS resolution in javascript
 * to get these results.
 *
 * @param category
 */
export function getPayoutColor(category: PayoutCategory): string {
  switch (category) {
    case PayoutCategory.Salary:
    case PayoutCategory.ReducedSalary:
      return '#515151';
    case PayoutCategory.ExtraIncome:
      return '#ccc';

    case PayoutCategory.LGU:
    case PayoutCategory.IVA:
      return '#5db957';
    case PayoutCategory.VVU:
    case PayoutCategory.LAU:
      return '#306d2d';

    case PayoutCategory.WBO:
    case PayoutCategory.WHA:
      return '#ffdb00';
    case PayoutCategory.WEB:
    case PayoutCategory.WEX:
      return '#ff7a00';
    case PayoutCategory.WET:
      return '#ff9d00';

    default:
      return '#515151';
  }
}
